import { Badge } from "../../../ui/common-ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../ui/common-ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../../ui/common-ui/collapsible";
import {
  AlertTriangle,
  Clock,
  GitBranch,
  Globe,
  Key,
  Network,
  Shield,
  ChevronDown,
} from "lucide-react";
import { useState } from "react";
import type { GraphNode } from "../types/graph";

interface CorrelationInspectorProps {
  node: GraphNode;
}

export function CorrelationInspector({ node }: CorrelationInspectorProps) {
  const info = node.props?.correlationInfo;
  const [cloudLogOpen, setCloudLogOpen] = useState(false);
  const [saasLogOpen, setSaasLogOpen] = useState(false);

  if (!info) {
    return (
      <Card className="border-border/50 bg-card shadow-lg">
        <CardContent className="p-6 text-center text-muted-foreground">
          <GitBranch className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-xs">No correlation details available</p>
        </CardContent>
      </Card>
    );
  }

  const severityStyles: Record<string, string> = {
    CRITICAL: "bg-red-500/20 text-red-500 border-red-500/30",
    HIGH: "bg-orange-500/20 text-orange-500 border-orange-500/30",
    MEDIUM: "bg-yellow-500/20 text-yellow-500 border-yellow-500/30",
    LOW: "bg-blue-500/20 text-blue-500 border-blue-500/30",
  };

  const severity = node.props?.severity || "HIGH";
  const saasType = info.saasType || "GITHUB";

  const formatTime = (ts: string) => {
    if (!ts) return "N/A";
    return new Date(ts).toLocaleString();
  };

  return (
    <div className="space-y-4">
      {/* Summary */}
      <Card className="border-border/50 bg-card shadow-lg">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-orange-500/10">
              <GitBranch className="w-4 h-4 text-orange-500" />
            </div>
            Correlation Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-foreground leading-relaxed">{info.summary}</p>

          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className={`text-[10px] font-semibold ${severityStyles[severity] || severityStyles.HIGH}`}>
              {severity}
            </Badge>
            <Badge variant="secondary" className="text-[10px]">
              {saasType}
            </Badge>
            {node.props?.status && (
              <Badge variant="outline" className="text-[10px]">
                {node.props.status}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card className="border-border/50 bg-card shadow-lg">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-blue-500/10">
              <Clock className="w-4 h-4 text-blue-500" />
            </div>
            Event Timeline
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-border/40">
            <Key className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-medium text-muted-foreground">Cloud Secret Access</p>
              <p className="text-xs font-mono">{formatTime(info.cloudEventTimeUtc)}</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-border/40">
            <Globe className="w-4 h-4 text-primary shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-medium text-muted-foreground">SaaS Activity ({saasType})</p>
              <p className="text-xs font-mono">{formatTime(info.saasEventTimeUtc)}</p>
            </div>
          </div>
          {info.timeDeltaSeconds != null && (
            <div className="text-center text-[10px] text-muted-foreground font-medium">
              Time delta: <span className="font-bold text-foreground">{info.timeDeltaSeconds}s</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Context */}
      {info.context && (
        <Card className="border-border/50 bg-card shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-primary/10">
                <Network className="w-4 h-4 text-primary" />
              </div>
              Context
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(info.context).map(([key, value]) => (
              <div key={key} className="flex items-start gap-2 p-2 rounded-lg bg-muted/20 border border-border/30">
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide min-w-[100px] shrink-0">
                  {key.replace(/([A-Z])/g, " $1").trim()}
                </span>
                <span className="text-xs text-foreground break-all">{String(value)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Cloud Log */}
      {info.cloudLog && (
        <Collapsible open={cloudLogOpen} onOpenChange={setCloudLogOpen}>
          <Card className="border-border/50 bg-card shadow-lg overflow-hidden">
            <CollapsibleTrigger className="flex items-center justify-between w-full hover:bg-muted/50 p-4 transition-colors">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-green-500/10">
                  <Shield className="w-4 h-4 text-green-500" />
                </div>
                Cloud Audit Log
              </CardTitle>
              <ChevronDown className={`w-4 h-4 transition-transform ${cloudLogOpen ? "rotate-180" : ""}`} />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                <pre className="text-[10px] font-mono bg-muted/30 p-3 rounded-lg border border-border/30 overflow-x-auto max-h-60 overflow-y-auto whitespace-pre-wrap break-all">
                  {JSON.stringify(info.cloudLog, null, 2)}
                </pre>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      {/* SaaS Log */}
      {info.saasLog && (
        <Collapsible open={saasLogOpen} onOpenChange={setSaasLogOpen}>
          <Card className="border-border/50 bg-card shadow-lg overflow-hidden">
            <CollapsibleTrigger className="flex items-center justify-between w-full hover:bg-muted/50 p-4 transition-colors">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-purple-500/10">
                  <Globe className="w-4 h-4 text-purple-500" />
                </div>
                {saasType} Activity Log
              </CardTitle>
              <ChevronDown className={`w-4 h-4 transition-transform ${saasLogOpen ? "rotate-180" : ""}`} />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                <pre className="text-[10px] font-mono bg-muted/30 p-3 rounded-lg border border-border/30 overflow-x-auto max-h-60 overflow-y-auto whitespace-pre-wrap break-all">
                  {JSON.stringify(info.saasLog, null, 2)}
                </pre>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}
    </div>
  );
}
